import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import PageLayout from '../components/layout/PageLayout';
import SEOHead from '../components/shared/SEOHead';

const stages = [
  { num: '01', title: 'Understand the organisation', desc: 'The first step is learning how the business works, what it is trying to achieve and where leaders already see pressure. This usually happens through an initial conversation.' },
  { num: '02', title: 'Gather evidence', desc: 'Depending on what is needed, Kalobrate may use employee surveys, confidential listening sessions, leadership conversations and existing workplace data. All employee input is treated confidentially.' },
  { num: '03', title: 'Identify pressure', desc: 'The evidence is reviewed to find where culture is helping performance and where it may be creating risk. The aim is to identify patterns rather than treat each symptom separately.' },
  { num: '04', title: 'Agree priorities', desc: 'Kalobrate works with leaders to decide what matters most, what is realistic and where action will have the most effect. Not everything needs to change at once.' },
  { num: '05', title: 'Support the response', desc: 'This may include leadership support, manager development, workforce learning, speaking or ongoing guidance. The response is shaped by the findings, not a fixed programme.' },
  { num: '06', title: 'Measure and learn', desc: 'Where possible, progress is reviewed against the original findings. This helps leaders understand what is working, what needs to adjust and where to focus next.' },
];

const HowWeWorkPage = () => (
  <PageLayout>
    <SEOHead
      title="How We Work | Kalobrate"
      description="Learn how Kalobrate helps organisations understand workplace culture through evidence gathering, leadership conversations, employee listening and practical recommendations."
      canonical="/how-we-work"
    />

    <section className="bg-carbon py-20 md:py-28 relative overflow-hidden">
      <img src="/assets/kalobrate-icon-outline-yellow.png" alt="" aria-hidden="true" className="absolute top-[-10%] right-[-5%] w-[55%] h-[120%] object-contain opacity-[0.06] pointer-events-none select-none" />
      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white leading-tight tracking-tight mb-6" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          A clear route from <span className="yellow-highlight text-carbon">concern</span> to action
        </motion.h1>
        <motion.p className="text-base text-white/80 leading-relaxed" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
          The exact work changes from one organisation to another, but the principle stays the same: understand before acting.
        </motion.p>
      </div>
    </section>

    <section className="py-16 md:py-24">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl font-heading font-bold text-carbon mb-4 tracking-tight">How the first conversation works</h2>
        <p className="text-base text-carbon/80 leading-relaxed mb-4">Most work begins with a conversation. You do not need to arrive with a fully defined brief or a detailed understanding of the problem.</p>
        <p className="text-base text-carbon/80 leading-relaxed mb-12">The first conversation usually covers what is prompting the enquiry, what the organisation looks like, what leaders have already noticed and whether Kalobrate is likely to be a useful fit.</p>

        <div className="space-y-10 mb-16">
          {stages.map((s, i) => (
            <motion.div
              key={s.num}
              className="flex gap-6"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
            >
              <span className="text-3xl font-heading font-bold text-golden flex-shrink-0 w-12">{s.num}</span>
              <div>
                <h3 className="text-lg font-heading font-semibold text-carbon mb-2">{s.title}</h3>
                <p className="text-sm text-carbon/75 leading-relaxed">{s.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="bg-gray-50 border border-gray-100 p-6 md:p-8 mb-16">
          <h2 className="text-xl font-heading font-bold text-carbon mb-4 tracking-tight">What the client receives</h2>
          <ul className="space-y-3 text-sm text-carbon/75">
            {['A clear account of what is working and what needs attention', 'Evidence from employees, leaders and existing workplace data', 'Priority culture risks and pressure points', 'Practical recommendations', 'A leadership discussion or action-planning session', 'A way to measure whether changes are working'].map(item => (
              <li key={item} className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 bg-golden rounded-full mt-2 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-gray-50 border border-gray-100 p-6 md:p-8 mb-16">
          <h2 className="text-xl font-heading font-bold text-carbon mb-4 tracking-tight">Employee confidentiality</h2>
          <p className="text-sm text-carbon/75 leading-relaxed mb-3">All employee input is treated confidentially. Individuals are not identified in reports or recommendations unless they have given explicit permission.</p>
          <p className="text-sm text-carbon/75 leading-relaxed">Employees are told clearly at the start of each session how information will be used and what they can expect to happen as a result.</p>
        </div>
      </div>
    </section>

    <section className="bg-golden py-16">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-2xl sm:text-3xl font-heading font-bold text-carbon mb-4 tracking-tight">Not sure where to start?</h2>
        <p className="text-base text-carbon/80 mb-8 max-w-xl mx-auto leading-relaxed">A first conversation can help clarify what you already know, what evidence may be missing and whether Kalobrate is the right support.</p>
        <Link to="/contact" className="bg-carbon text-white px-7 py-3.5 font-semibold text-sm hover:bg-carbon/90 transition-colors inline-flex items-center">
          Talk to Ollie <ArrowRight className="ml-2 w-4 h-4" />
        </Link>
      </div>
    </section>
  </PageLayout>
);

export default HowWeWorkPage;
